import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import {
  getAdvertiserFromCookie,
  getOrganisationFromCookie,
  getTenantFromCookie,
  saveOperationalContextStateInCookie,
} from "../utils";
import getNextFromUrlSearchParameters from "../utils/location";
import { isImpersonating, isInternalUser } from "../utils/userHelper";
import { OperationalContextState, PortalUserRead } from "./coreApi";

const operationalContextSlice = createSlice({
  name: "operationalContext",
  initialState: null as OperationalContextState | null,
  reducers: {
    setOperationalContextFromAccount: (
      _,
      { payload: account }: PayloadAction<PortalUserRead>
    ) => {
      let operationalContextState;
      if (account.userInfo) {
        if (isInternalUser(account) || isImpersonating()) {
          operationalContextState = getOperationalContextStateFromCookie();
        }
        if (!operationalContextState) {
          operationalContextState =
            getOperationalContextStateFromAccount(account);
        }
      }
      saveOperationalContextStateInCookie(operationalContextState);
      return operationalContextState;
    },
    setOperationalContext: (
      state,
      {
        payload: operationalContext,
      }: PayloadAction<OperationalContextState | undefined>
    ) => {
      saveOperationalContextStateInCookie(operationalContext);
      // if there was a previous state and the new state is different, then we reload the window
      if (
        state &&
        operationalContext &&
        (operationalContext.tenant !== state.tenant ||
          operationalContext.organisationCode !== state.organisationCode ||
          operationalContext.advertiserCode !== state.advertiserCode)
      ) {
        const nextUrl = getNextFromUrlSearchParameters();
        if (nextUrl) {
          window.location.href = nextUrl;
        } else {
          window.location.reload();
        }
      }
    },
  },
});

// Returns an operational context state built from data found in cookies
const getOperationalContextStateFromCookie = ():
  | OperationalContextState
  | undefined => {
  const tenantFromCookie = getTenantFromCookie();
  if (!tenantFromCookie) {
    return undefined;
  }

  const organisationFromCookie = getOrganisationFromCookie();
  if (!organisationFromCookie) {
    return undefined;
  }

  const advertiserFromCookie = getAdvertiserFromCookie();
  const result: OperationalContextState = {
    tenant: tenantFromCookie,
    organisationCode: organisationFromCookie,
    advertiserCode: advertiserFromCookie,
  };
  return result;
};

// Returns an operational context state built from data found in the account
const getOperationalContextStateFromAccount = (
  account: PortalUserRead
): OperationalContextState => ({
  tenant: account.userInfo.tenant,
  organisationCode: account.userInfo.organisationCode,
  advertiserCode: account.userInfo.advertiserCode,
});

// Action creators are generated for each case reducer function
export const { setOperationalContextFromAccount, setOperationalContext } =
  operationalContextSlice.actions;

export default operationalContextSlice.reducer;
